import { ComponentType } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { makeStyles, mergeClasses } from "@fluentui/react-components";
import {
  AlertRegular,
  ArrowLeftRegular,
  BriefcaseRegular,
  BuildingRegular,
  ClipboardRegular,
  LinkRegular,
  MailTemplateRegular,
  PersonKeyRegular,
  TagMultipleRegular,
  TagRegular,
} from "@fluentui/react-icons";
import { projectIqTokens } from "apps/project-iq/theme/projectIqTokens";
import { designTokens } from "theme/customTheme";
import { ManageIqCapabilities } from "apps/project-iq/components/admin/ManageIqCapabilities/ManageIqCapabilities";
import {
  getProjectIqNotifications,
  markProjectIqNotificationSeen,
} from "apps/project-iq/api";
import { AnnouncementsBanner } from "components/manageAnnouncements/announcementsBanner/AnnouncementsBanner";
import { ManageIqNotifications } from "apps/project-iq/components/admin/ManageIqNotifications/ManageIqNotifications";
import { ManageIqWorkspaces } from "apps/project-iq/components/admin/ManageIqWorkspaces/ManageIqWorkspaces";
import { ManageSurveyTemplates } from "apps/project-iq/components/admin/ManageSurveyTemplates/ManageSurveyTemplates";
import { ManageIqLinks } from "apps/project-iq/components/admin/ManageIqLinks/ManageIqLinks";
import { ManageContextFileTags } from "apps/project-iq/components/admin/ManageContextFileTags/ManageContextFileTags";
import { ManageDeliveryMethods } from "apps/project-iq/components/admin/ManageDeliveryMethods/ManageDeliveryMethods";
import { ManagePortfoliosPractices } from "apps/project-iq/components/admin/ManagePortfoliosPractices/ManagePortfoliosPractices";
import { PortfoliosPracticesAccess } from "apps/project-iq/components/admin/PortfoliosPracticesAccess/PortfoliosPracticesAccess";

const markNotificationSeen = async (id: string): Promise<void> => {
  await markProjectIqNotificationSeen(id);
};

const NAV_ITEMS = [
  { id: "capabilities", label: "Capabilities", icon: BriefcaseRegular },
  { id: "workspaces", label: "Workspaces", icon: BuildingRegular },
  { id: "links", label: "Links", icon: LinkRegular },
  { id: "notifications", label: "Notifications", icon: AlertRegular },
  {
    id: "surveyTemplates",
    label: "Survey Templates",
    icon: ClipboardRegular,
  },
  {
    id: "deliveryMethods",
    label: "Delivery Methods",
    icon: MailTemplateRegular,
  },
  {
    id: "portfoliosPractices",
    label: "Portfolios & Practices",
    icon: TagRegular,
  },
  {
    id: "portfoliosPracticesAccess",
    label: "Portfolio & Practices access",
    icon: PersonKeyRegular,
  },
  {
    id: "contextFileTags",
    label: "Context Files Tags",
    icon: TagMultipleRegular,
  },
] as const;

type NavItemId = (typeof NAV_ITEMS)[number]["id"];

const SECTION_COMPONENTS: Record<NavItemId, ComponentType> = {
  capabilities: ManageIqCapabilities,
  workspaces: ManageIqWorkspaces,
  links: ManageIqLinks,
  notifications: ManageIqNotifications,
  surveyTemplates: ManageSurveyTemplates,
  deliveryMethods: ManageDeliveryMethods,
  portfoliosPractices: ManagePortfoliosPractices,
  portfoliosPracticesAccess: PortfoliosPracticesAccess,
  contextFileTags: ManageContextFileTags,
};

const useStyles = makeStyles({
  root: {
    display: "flex",
    flexDirection: "row",
    height: "100vh",
  },
  sidebar: {
    width: projectIqTokens.sidebarWidth,
    flexShrink: 0,
    backgroundColor: projectIqTokens.sidebarBg,
    borderRight: `1px solid ${projectIqTokens.sidebarBorder}`,
    display: "flex",
    flexDirection: "column",
  },
  sidebarHeading: {
    fontSize: designTokens.fontSize.base,
    fontWeight: designTokens.fontWeight.semibold,
    color: projectIqTokens.teal,
    padding: designTokens.spacing[4],
    margin: "0",
  },
  navItem: {
    display: "flex",
    alignItems: "center",
    gap: designTokens.spacing[2],
    padding: `${designTokens.spacing[2]} ${designTokens.spacing[4]}`,
    cursor: "pointer",
    fontSize: designTokens.fontSize.sm,
    color: projectIqTokens.sidebarFadedText,
    borderLeft: "3px solid transparent",
    ":hover": {
      backgroundColor: projectIqTokens.sidebarHover,
    },
    ":focus-visible": {
      outlineWidth: "2px",
      outlineStyle: "solid",
      outlineColor: projectIqTokens.teal,
      outlineOffset: "-2px",
    },
  },
  navItemActive: {
    backgroundColor: projectIqTokens.sidebarSelected,
    color: designTokens.neutral.white,
    borderLeft: `3px solid ${projectIqTokens.teal}`,
    ":focus-visible": {
      outlineWidth: "2px",
      outlineStyle: "solid",
      outlineColor: projectIqTokens.teal,
      outlineOffset: "-2px",
    },
  },
  sidebarBack: {
    display: "flex",
    alignItems: "center",
    gap: designTokens.spacing[2],
    padding: `${designTokens.spacing[3]} ${designTokens.spacing[4]}`,
    cursor: "pointer",
    fontSize: designTokens.fontSize.sm,
    color: projectIqTokens.sidebarFadedText,
    borderBottom: `1px solid ${projectIqTokens.sidebarBorder}`,
    marginBottom: designTokens.spacing[2],
    ":hover": {
      backgroundColor: projectIqTokens.sidebarHover,
      color: designTokens.neutral.white,
    },
    ":focus-visible": {
      outlineWidth: "2px",
      outlineStyle: "solid",
      outlineColor: projectIqTokens.teal,
      outlineOffset: "-2px",
    },
  },
  content: {
    flex: 1,
    display: "flex",
    flexDirection: "column",
    overflow: "hidden",
    minWidth: 0,
  },
  contentBody: {
    flex: 1,
    overflow: "auto",
  },
});

export const ProjectIqAdminPage = (): JSX.Element => {
  const { section } = useParams<{ section?: string }>();
  const navigate = useNavigate();
  const styles = useStyles();

  const activeSection: NavItemId = NAV_ITEMS.some((item) => item.id === section)
    ? (section as NavItemId)
    : "capabilities";
  const ActiveSectionComponent = SECTION_COMPONENTS[activeSection];

  return (
    <div className={styles.root}>
      <aside className={styles.sidebar}>
        <h2 className={styles.sidebarHeading}>Project IQ Admin</h2>
        <div
          className={styles.sidebarBack}
          onClick={() => navigate("/")}
          role="button"
          tabIndex={0}
          aria-label="Back to Project IQ"
          onKeyDown={(e) => {
            if (e.key === "Enter" || e.key === " ") navigate("/");
          }}
        >
          <ArrowLeftRegular />
          Back to Project IQ
        </div>
        {NAV_ITEMS.map((item) => {
          const Icon = item.icon;
          const isActive = activeSection === item.id;
          return (
            <div
              key={item.id}
              className={mergeClasses(
                styles.navItem,
                isActive && styles.navItemActive,
              )}
              onClick={() => navigate(`/admin/${item.id}`)}
              role="button"
              tabIndex={0}
              aria-current={isActive ? "page" : undefined}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                  navigate(`/admin/${item.id}`);
                }
              }}
            >
              <Icon />
              {item.label}
            </div>
          );
        })}
      </aside>
      <main className={styles.content}>
        <AnnouncementsBanner
          fetchFn={getProjectIqNotifications}
          markSeenFn={markNotificationSeen}
        />
        <div className={styles.contentBody}>
          <ActiveSectionComponent />
        </div>
      </main>
    </div>
  );
};
